/**
 * speakerKey.js (client) — the speaker-identity fold, for comparing a RAW speaker label to a key
 * the server already folded.
 *
 * ⛔ WHY THIS EXISTS AT ALL, AND WHAT IT IS NOT FOR.
 *
 * The one authoritative rule lives in `server/utilities/speakerKey.js`. It has folded typographic
 * apostrophes since 2026-08-10, after the FS1 canary import made two characters out of
 * "the Mayor’s assistant" (U+2019) and "the Mayor's assistant" (U+0027). The client cannot import
 * it, so this is a deliberate second copy — kept to ONE copy, in ONE file, rather than re-invented
 * per component, which is exactly how the server accumulated four private versions of the same
 * rule and shipped a book with a silent line in it.
 *
 * ⭐ PREFER A SERVER-SUPPLIED KEY WHENEVER ONE IS ON THE WIRE. The preflight already sends
 * `normName` per speaker — the server's own fold — so the authoritative side of any comparison
 * should be that field, and this function should only ever be applied to the RAW label the client
 * holds (a line's `speaker`, straight from `speakerRaw`). Do not use it to re-derive something the
 * server already told you.
 *
 * ⚠️ IF THE SERVER RULE CHANGES, THIS MUST CHANGE WITH IT. The two are pinned by
 * `speakerKey.parity.test.js`, which reads both files and compares their folds on real corpus
 * strings — so a divergence fails the suite rather than surfacing as a wrong badge months later.
 *
 * ⓘ Scope note: the server rule ALSO refuses to fold articles and honorifics, on purpose
 * ("Rakow", "Vonson Rakow" and "Mr Vonson Rakow" stay three identities). Nothing here should ever
 * grow cleverer than the server's version.
 */

/** Every apostrophe-shaped codepoint, normalised to the plain one. */
const APOSTROPHES = /[‘’ʼʹ՚`´]/g;
const DOUBLE_QUOTES = /[“”]/g;

export function normalizeSpeakerName(name) {
  if (name === null || name === undefined) return '';
  return String(name)
    .replace(APOSTROPHES, "'")
    .replace(DOUBLE_QUOTES, '"')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export default normalizeSpeakerName;
